// SPDX-License-Identifier: AGPL-3.0-or-later

import type {VoiceEngineApi, VoiceEngineVideoFrame} from '@app/features/platform/types/Electron';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	createInboundVideoBridge,
	type InboundVideoBridgeHandle,
} from '@app/features/voice/engine/native_voice_engine/createInboundVideoBridge';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('NativeInboundVideoManager');

export interface NativeInboundVideoTrack {
	participantSid: string;
	trackSid: string;
	source: string;
	width: number;
	height: number;
	stream: MediaStream;
}

interface InboundVideoEntry {
	participantSid: string;
	trackSid: string;
	source: string;
	bridge: InboundVideoBridgeHandle;
}

function frameKey(frame: VoiceEngineVideoFrame): string {
	return frame.meta.trackSid;
}

class NativeInboundVideoManager {
	private _tracks: Readonly<Record<string, NativeInboundVideoTrack>> = {};
	private entries = new Map<string, InboundVideoEntry>();
	private unsubscribeFrames: (() => void) | null = null;

	constructor() {
		makeAutoObservable<NativeInboundVideoManager, 'entries'>(this, {entries: false}, {autoBind: true});
	}

	get tracks(): Readonly<Record<string, NativeInboundVideoTrack>> {
		return this._tracks;
	}

	getTracksForParticipant(participantSid: string): Array<NativeInboundVideoTrack> {
		return Object.values(this._tracks).filter((track) => track.participantSid === participantSid);
	}

	start(bridge: VoiceEngineApi): void {
		this.stopFrameSubscription();
		this.unsubscribeFrames = bridge.onVideoFrame((frame) => this.handleFrame(frame));
		logger.info('Inbound video manager started');
	}

	registerTrack(participantSid: string, trackSid: string, source: string): void {
		if (this.entries.has(trackSid)) return;
		const bridge = createInboundVideoBridge(`${participantSid}:${trackSid}`);
		if (!bridge) {
			logger.warn('Could not build inbound video bridge; tile will be unavailable', {participantSid, trackSid});
			return;
		}
		const entry: InboundVideoEntry = {participantSid, trackSid, source, bridge};
		this.entries.set(trackSid, entry);
		runInAction(() => {
			this._tracks = {
				...this._tracks,
				[trackSid]: {
					participantSid,
					trackSid,
					source,
					width: 0,
					height: 0,
					stream: bridge.stream,
				},
			};
		});
		logger.debug('Registered inbound video track', {participantSid, trackSid, source});
	}

	unregisterTrack(trackSid: string): void {
		const entry = this.entries.get(trackSid);
		if (!entry) return;
		this.entries.delete(trackSid);
		void entry.bridge.cleanup().catch((error) => {
			logger.warn('Failed to clean up inbound video bridge', {trackSid, error});
		});
		runInAction(() => {
			if (!(trackSid in this._tracks)) return;
			const next = {...this._tracks};
			delete next[trackSid];
			this._tracks = next;
		});
		logger.debug('Unregistered inbound video track', {trackSid});
	}

	unregisterParticipant(participantSid: string): void {
		for (const entry of [...this.entries.values()]) {
			if (entry.participantSid === participantSid) {
				this.unregisterTrack(entry.trackSid);
			}
		}
	}

	clear(): void {
		this.stopFrameSubscription();
		for (const entry of [...this.entries.values()]) {
			this.unregisterTrack(entry.trackSid);
		}
	}

	private stopFrameSubscription(): void {
		this.unsubscribeFrames?.();
		this.unsubscribeFrames = null;
	}

	private handleFrame(frame: VoiceEngineVideoFrame): void {
		const key = frameKey(frame);
		const entry = this.entries.get(key);
		if (!entry) {
			return;
		}
		entry.bridge.pushFrame({
			width: frame.meta.width,
			height: frame.meta.height,
			timestampUs: frame.meta.timestampUs,
			data: frame.data,
		});
		const existing = this._tracks[key];
		if (existing && (existing.width !== frame.meta.width || existing.height !== frame.meta.height)) {
			runInAction(() => {
				this._tracks = {
					...this._tracks,
					[key]: {...existing, width: frame.meta.width, height: frame.meta.height},
				};
			});
		}
	}
}

const instance = new NativeInboundVideoManager();

(
	window as typeof window & {
		_nativeInboundVideoManager?: NativeInboundVideoManager;
	}
)._nativeInboundVideoManager = instance;

export default instance;
export {NativeInboundVideoManager};
